import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';

const initialState = {};

export const clientSliceV2 = createSlice({
	name: 'clientStateV2',
	initialState,
	reducers: {},
});

// Constants
const ERROR_MSG_CLIENT_DATA_REQUIRED = 'Client data is required';
const ERROR_MSG_CLIENT_ID_REQUIRED = 'Client ID is required for update';
const ERROR_MSG_CLIENT_ID_REQUIRED_GENERIC = 'Client ID is required';

/**
 * Helper: Transform head_office_address from string to object
 * @param {Object} data - Form data with head_office_address as string
 * @returns {Object} Address object for API
 */
const transformHeadOfficeAddress = (data) => {
	if (!data) {
		return {
			address_line1: '',
			city: '',
			state: '',
			pincode: '',
			zone: '',
		};
	}

	if (typeof data.head_office_address === 'object' && data.head_office_address !== null) {
		return {
			address_line1: data.head_office_address.address_line1 || '',
			city: data.head_office_address.city || '',
			state: data.head_office_address.state || '',
			pincode: data.head_office_address.pincode || '',
			zone: data.head_office_address.zone || '',
		};
	}

	return {
		address_line1: data.head_office_address || '',
		city: data.city || '',
		state: data.state || '',
		pincode: data.pincode || '',
		zone: data.zone || '',
	};
};

/**
 * Helper: Transform additional document to business document format
 * @param {Object} doc - Additional document object
 * @returns {Object|null} Transformed document entry or null if invalid
 */
const transformAdditionalDocument = (doc) => {
	if (!doc.document_type || !doc.document_number || !doc.document_file_key) {
		return null;
	}

	const documentEntry = {
		document_type: doc.document_type,
		document_number: doc.document_number.trim(),
		document_file_key: doc.document_file_key,
	};

	// Add type-specific fields based on document type
	if (doc.document_type === 'TAN') {
		documentEntry.tan_number = doc.document_number.trim();
		documentEntry.tan_file_key = doc.document_file_key;
	} else if (doc.document_type === 'CIN') {
		documentEntry.cin_number = doc.document_number.trim();
		documentEntry.cin_file_key = doc.document_file_key;
	}

	return documentEntry;
};

/**
 * Helper: Transform document_info to business_documents array
 * @param {Object} data - Form data
 * @returns {Array} Business documents array for API
 */
const transformBusinessDocuments = (data) => {
	const documents = [];

	if (!data) {
		return documents;
	}

	// Add PAN document
	if (data.pan_number && data.pan_number.trim()) {
		documents.push({
			document_type: 'PAN',
			pan_number: data.pan_number.trim(),
			pan_file_key: data.pan_file_key || '', // Use S3 file key
		});
	}

	// Add GST document only for Registered type (Unregistered doesn't need document in array)
	if (data.gst_type === 'Registered' && data.gst_number && data.gst_number.trim()) {
		documents.push({
			document_type: 'GST',
			gst_type: data.gst_type,
			gst_number: data.gst_number.trim(),
			gst_file_key: data.gst_file_key || '',
		});
	}

	// Add additional documents (TAN, CIN, etc.)
	if (data.additional_documents && Array.isArray(data.additional_documents)) {
		data.additional_documents.forEach((doc) => {
			const transformedDoc = transformAdditionalDocument(doc);
			if (transformedDoc) {
				documents.push(transformedDoc);
			}
		});
	}

	return documents;
};

/**
 * Helper: Filter valid billing addresses
 * @param {Array} addresses - Array of billing addresses
 * @returns {Array} Filtered addresses
 */
const filterValidBillingAddresses = (addresses) =>
	Array.isArray(addresses) ? addresses.filter((addr) => addr && addr.address_line1) : [];

/**
 * Helper: Parse credit period
 * @param {string|number} creditPeriod - Credit period value
 * @returns {number} Parsed credit period
 */
const parseCreditPeriod = (creditPeriod) => (creditPeriod ? parseInt(creditPeriod, 10) : 0);

/**
 * Helper: Validate and convert client ID to integer
 * @param {string|number} id - Client ID to validate
 * @returns {number} Validated numeric ID
 * @throws {Error} If ID is invalid
 */
const validateClientId = (id) => {
	if (!id) {
		throw new Error(ERROR_MSG_CLIENT_ID_REQUIRED);
	}
	return id;
};

/**
 * Helper: Build complete API payload from form data
 * @param {Object} formData - Raw form data
 * @param {boolean} isUpdate - Whether this is an update operation
 * @returns {Object} Transformed data for API
 */
const buildClientPayload = (formData, isUpdate = false) => {
	if (!formData) {
		throw new Error('Form data is required');
	}

	if (isUpdate) {
		validateClientId(formData.id);
	}

	const payload = {
		client_legal_name: formData.client_legal_name || '',
		company_name: formData.company_name || '',
		legal_structure: formData.legal_structure || '',
		primary_cost_center: formData.primary_cost_center || '',
		primary_poc_name: formData.primary_poc_name || '',
		primary_poc_contact: formData.primary_poc_contact || '',
		finance_poc_name: formData.finance_poc_name || '',
		finance_poc_contact: formData.finance_poc_contact || '',
		email_id: formData.email_id || '',
		head_office_address: transformHeadOfficeAddress(formData),
		billing_addresses: filterValidBillingAddresses(formData.billing_addresses),
		business_documents: transformBusinessDocuments(formData),
		gst_type: formData.gst_type || '', // Add gst_type as top-level field
		credit_period: parseCreditPeriod(formData.credit_period),
		crf_file_key: formData.crf_file_key || '', // Use S3 file key
	};

	if (isUpdate) {
		// Convert id to integer (it comes as string from URL params)
		payload.id = validateClientId(formData.id);
	}

	return payload;
};

/**
 * Create Client V2
 * @param {Object} params
 * @param {Object} params.req_data - Client data matching API contract
 * @param {Function} params.setErrors - Form error setter function
 * @returns {Promise} API response with created client data
 */
export const createClientV2 =
	({ req_data, setErrors }) =>
	async () => {
		if (!req_data) {
			toaster.error(ERROR_MSG_CLIENT_DATA_REQUIRED);
			return Promise.reject(new Error(ERROR_MSG_CLIENT_DATA_REQUIRED));
		}

		try {
			const transformedData = buildClientPayload(req_data, false);

			const data = {
				create_type: 'client',
				data: transformedData,
			};

			const CREDENTIALS = {
				url: '/create',
				data,
				setErrors,
				version: 'v2',
			};

			return API.common(CREDENTIALS).then((res) => {
				toaster.success('Client created successfully');
				return res;
			});
		} catch (error) {
			toaster.error(error.message || 'Failed to create client');
			return Promise.reject(error);
		}
	};

/**
 * Update Client V2
 * @param {Object} params
 * @param {Object} params.req_data - Client data to update (must include id field)
 * @param {Function} params.setErrors - Form error setter function
 * @returns {Promise} API response with updated client data
 */
export const updateClientV2 =
	({ req_data, setErrors }) =>
	async () => {
		if (!req_data) {
			toaster.error(ERROR_MSG_CLIENT_DATA_REQUIRED);
			return Promise.reject(new Error(ERROR_MSG_CLIENT_DATA_REQUIRED));
		}

		if (!req_data.id) {
			toaster.error(ERROR_MSG_CLIENT_ID_REQUIRED);
			return Promise.reject(new Error(ERROR_MSG_CLIENT_ID_REQUIRED));
		}

		try {
			const transformedData = buildClientPayload(req_data, true);

			const data = {
				update_type: 'client',
				data: transformedData,
			};

			const CREDENTIALS = {
				url: '/update',
				data,
				setErrors,
				version: 'v2',
			};

			return API.common(CREDENTIALS).then((res) => {
				toaster.success('Client updated successfully');
				return res;
			});
		} catch (error) {
			toaster.error(error.message || 'Failed to update client');
			return Promise.reject(error);
		}
	};

/**
 * Update Client Status (Active/Inactive)
 * @param {Object} params
 * @param {number} params.id - Client ID
 * @param {boolean} params.is_active - New status (true for Active, false for Inactive)
 * @returns {Promise} API response
 */
export const updateClientStatus =
	({ id, is_active }) =>
	async () => {
		if (!id) {
			toaster.error(ERROR_MSG_CLIENT_ID_REQUIRED_GENERIC);
			return Promise.reject(new Error(ERROR_MSG_CLIENT_ID_REQUIRED_GENERIC));
		}

		try {
			const data = {
				update_type: 'client',
				data: {
					id,
					is_active,
				},
			};

			const CREDENTIALS = {
				url: '/update',
				data,
				version: 'v2',
			};

			return API.common(CREDENTIALS).then((res) => {
				toaster.success(`Client ${is_active ? 'activated' : 'deactivated'} successfully`);
				return res;
			});
		} catch (error) {
			toaster.error(error.message || 'Failed to update client status');
			return Promise.reject(error);
		}
	};

/**
 * Blacklist Client V2
 * @param {Object} params
 * @param {number} params.id - Client ID
 * @param {boolean} params.is_blacklisted - Blacklist status (true to blacklist, false to unblacklist)
 * @returns {Promise} API response
 */
/**
 * Blacklist Client V2
 * @param {Object} params
 * @param {number} params.id - Client ID
 * @param {boolean} params.is_blacklisted - Blacklist status (true to blacklist, false to unblacklist)
 * @returns {Promise} API response
 */
export const blacklistClientV2 =
	({ id, is_blacklisted = true }) =>
	async () => {
		if (!id) {
			toaster.error(ERROR_MSG_CLIENT_ID_REQUIRED_GENERIC);
			return Promise.reject(new Error(ERROR_MSG_CLIENT_ID_REQUIRED_GENERIC));
		}

		try {
			const data = {
				update_type: 'client',
				data: {
					id,
					is_blacklisted,
				},
			};

			const CREDENTIALS = {
				url: '/update',
				data,
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			if (response?.status === 'success') {
				toaster.success(
					response?.message ||
						`Client ${is_blacklisted ? 'blacklisted' : 'unblacklisted'} successfully`
				);
			} else {
				const errorMessage =
					response?.error?.message ||
					response?.message ||
					'Failed to update client blacklist status';
				toaster.error(errorMessage);
			}
			return response;
		} catch (error) {
			const errorMessage =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				'Failed to update client blacklist status. Please try again.';
			toaster.error(errorMessage);
			return Promise.reject(error);
		}
	};

/**
 * Get Client List V2 with filters and pagination
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria (all optional)
 * @param {Object} params.pagination - Pagination settings { page, per_page }
 * @param {Object} params.sort_by - Sort settings { field, order }
 * @returns {Promise} Object with { rows, pagination, count }
 */
export const getClientListV2 =
	({ filter_by = {}, pagination = { page: 1, per_page: 20 }, sort_by = {} }) =>
	async () => {
		// Build filter_by object, removing undefined/null values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					// query_str is a special parameter for general search
					// It searches across multiple fields (client_legal_name, company_name, email_id, etc.)
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client',
				filter_by: cleanFilterBy,
				pagination: {
					page: pagination?.page || 1,
					per_page: pagination?.per_page || 20,
				},
				...(sort_by && Object.keys(sort_by).length > 0 && { sort_by }),
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			rows: response?.data?.clients || [],
			pagination: response?.data?.pagination || {},
			count: response?.data?.pagination?.total || response?.data?.clients?.length || 0,
		}));
	};

/**
 * Get single client by ID
 * @param {Object} params
 * @param {string|number} params.id - Client ID
 * @returns {Promise} Client object or null
 */
export const getClientByIdV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client',
				filter_by: {
					id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const clients = response?.data?.clients || [];
			return clients.length > 0 ? clients[0] : null;
		});
	};

/**
 * Search Client Filters V2 - For dropdowns/autocomplete components
 * Endpoint: POST /api/v2/list_search
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria
 *   - search_term (string, optional): Searches in client_legal_name, company_name, email_id
 *   - business_unit_id (integer, optional)
 *   - is_active (boolean, optional)
 * @param {number} params.limit - Maximum results (default: 50, min: 1, max: 100)
 * @returns {Promise} Object with { results: [{ id, label, value, metadata }], total }
 */
export const searchClientFiltersV2 =
	({ filter_by = {}, limit = 50 }) =>
	async () => {
		// Build filter_by object, removing undefined/null values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		// Validate and clamp limit
		const validatedLimit = Math.min(Math.max(limit || 50, 1), 1000);

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client',
				filter_by: cleanFilterBy,
				limit: validatedLimit,
				offset: 0,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			results: (response?.data?.clients || []).map((item) => ({
				id: item.id,
				label: item.client_legal_name || item.company_name,
				value: item.id,
			})),
			total: response?.data?.pagination?.total || 0,
		}));
	};

/**
 * Export Client CSV V2
 * Endpoint: POST /api/v2/report
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria for export (same as Search Client filters)
 * @returns {Promise} Object with download_url or task_id
 */
export const exportClientCSV =
	({ filter_by = {} }) =>
	async () => {
		// Build filter_by object, removing undefined/null values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: '/report',
			data: {
				report_type: 'client_export',
				format: 'csv',
				data: {
					filter_by: cleanFilterBy,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// Handle direct download URL response
			if (response?.data?.download_url) {
				toaster.success(
					`Export ready! Downloading ${response.data.file_name || 'file'}...`
				);
				// Automatically trigger download
				const link = document.createElement('a');
				link.href = response.data.download_url;
				link.download = response.data.file_name || 'client_export.csv';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				return response.data;
			}
			// Handle async task response
			if (response?.data?.task_id) {
				toaster.info(
					`Export is being processed (Task ID: ${response.data.task_id}). You will be notified when ready.`
				);
				return response.data;
			}
			// Fallback - unexpected response format
			toaster.warning('Export response format unexpected. Please contact support.');
			return response?.data || {};
		});
	};

/**
 * Search Business Units - Fetch business units for filter dropdown
 * Note: Uses V1 API endpoint for business unit search (same as old client list)
 * @param {Object} params
 * @param {number} params.limit - Maximum results (default: 100)
 * @param {number} params.offset - Offset for pagination (default: 0)
 * @returns {Promise} Object with business units array
 */
export const searchBusinessUnits =
	({ limit = 100, offset = 0 }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'business_unit',
				offset,
				limit,
				fetch_filters: true,
				filter_list: {},
				filter_by: {},
			},
			// Using v1 API for business unit search
		};

		return API.common(CREDENTIALS).then((response) => ({
			rows: response?.data || [],
			count: response?.meta?.count || 0,
		}));
	};

/**
 * Search Pincode V2 - Auto-fetch zone, city, and state based on pincode
 * Note: Uses V1 API endpoint for pincode search
 * @param {Object} params
 * @param {string} params.pin_code - 6-digit pincode to search
 * @returns {Promise} Object with pincode data { zone, city, state, pincode }
 */
export const searchPincodeV2 =
	({ pincode }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'pin_code',
				filter_by: {
					pin_code: pincode, // API expects pin_code with underscore
				},
			},
			version: 'v1', // Explicitly set v1 for pincode search
		};

		return API.common(CREDENTIALS)
			.then((response) => {
				if (response?.data?.[0]) {
					return {
						zone: response.data[0].zone || '',
						city: response.data[0].city || '',
						city_code: response.data[0].city_code || '',
						state: response.data[0].state || '',
						pincode: response.data[0].pincode || pincode,
					};
				}
				toaster.error('Not a valid Pincode, please try something else!');
				return null;
			})
			.catch((error) => {
				toaster.error('Failed to fetch pincode details. Please try again.');
				throw error;
			});
	};

// ============ CLIENT CONTRACT FUNCTIONS ============

/**
 * Create Client Contract V2
 * @param {Object} params
 * @param {Object} params.req_data - Client contract data matching API contract
 * @param {Function} params.setErrors - Form error setter function
 * @returns {Promise} API response with created client contract data
 */
export const createClientContractV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			create_type: 'client_contract',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Client contract created. Pending approval.');
			return res;
		});
	};

/**
 * Get Client Contract List V2 with filters and pagination
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria (all optional)
 * @param {Object} params.pagination - Pagination settings { page, per_page }
 * @returns {Promise} Object with { rows, pagination, count }
 */
export const getClientContractListV2 =
	({ filter_by = {}, pagination = { page: 1, per_page: 20 } }) =>
	async () => {
		// Build filter_by object, removing undefined/null values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					// query_str is a special parameter for general search
					// It searches across multiple fields in client contracts
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		// Add business_unit_ids if not present (as per v1 API structure)
		if (!cleanFilterBy.business_unit_ids) {
			cleanFilterBy.business_unit_ids = [];
		}

		// Calculate offset from page number
		const page = pagination?.page || 1;
		const perPage = pagination?.per_page || 20;
		const offset = (page - 1) * perPage;

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_contract',
				offset: String(offset),
				limit: String(perPage),
				fetch_filters: true,
				filter_list: {},
				filter_by: cleanFilterBy,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			rows: response?.data?.contracts || [],
			pagination: response?.data?.pagination || {},
			count: response?.data?.pagination?.total || response?.data?.contracts?.length || 0,
		}));
	};

/**
 * Get single client contract by ID
 * @param {Object} params
 * @param {string|number} params.id - Client contract ID
 * @param {string|number} params.client_id - Client ID (optional, prioritized over id)
 * @returns {Promise} Client contract object or null
 */
export const getClientContractByIdV2 =
	({ id, client_id }) =>
	async () => {
		// Build filter_by - prioritize client_id if provided, otherwise use id
		const filterBy = {};
		if (client_id) {
			filterBy.client_id = client_id;
			// Also include id if available to ensure we get the specific contract
			if (id) {
				filterBy.id = id;
			}
		} else if (id) {
			filterBy.id = id;
		}

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_contract',
				filter_by: filterBy,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const contracts = response?.data?.contracts || [];
			return contracts.length > 0 ? contracts[0] : null;
		});
	};

/**
 * Search Client Contract Filters V2 - For dropdowns/autocomplete
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria
 * @param {number} params.limit - Maximum results (default: 50)
 * @returns {Promise} Object with { results, total }
 */
export const searchClientContractFiltersV2 =
	({ filter_by = {}, limit = 50 }) =>
	async () => {
		// Build filter_by object, removing undefined/null values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: '/list_search',
			data: {
				list_search_type: 'client_contract',
				filter_by: cleanFilterBy,
				limit: limit || 50,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			results: response?.data?.results || [],
			total: response?.data?.total || 0,
		}));
	};

/**
 * Update Client Contract V2
 * @param {Object} params
 * @param {Object} params.req_data - Client contract data to update (must include id field)
 * @param {boolean} [params.edit_flow] - If true, submits an edit-flow payload
 *   (id + changes + comments) for approval instead of a direct update.
 * @param {string} [params.comments] - Required when edit_flow=true.
 * @param {Object} [params.changes] - Required when edit_flow=true. Only the fields being changed.
 * @param {Function} params.setErrors - Form error setter function
 * @returns {Promise} API response with updated client contract data
 */
export const updateClientContractV2 =
	({ req_data, edit_flow = false, comments, changes, setErrors }) =>
	async () => {
		if (!req_data || !req_data.id) {
			toaster.error('Client contract ID is required for update');
			return Promise.reject(new Error('Client contract ID is required'));
		}

		const dataBody = edit_flow
			? {
					id: req_data.id,
					edit_flow: true,
					comments,
					changes,
			  }
			: req_data;

		const data = {
			update_type: 'client_contract',
			data: dataBody,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			const defaultMsg = edit_flow
				? 'Edit submitted for approval'
				: 'Client contract updated successfully';
			toaster.success(res?.message || defaultMsg);
			return res;
		});
	};

/**
 * Get Client Contract Edit History V2
 * @param {Object} params
 * @param {string|number} params.contract_id - Contract identifier
 * @param {string} [params.status] - Filter by status ("approved", "pending", "rejected")
 * @param {Object} [params.pagination] - { page, per_page }
 * @returns {Promise} { rows, count } shaped for RCTable
 */
export const getClientContractEditHistoryV2 =
	({ contract_id, status, pagination = { page: 1, per_page: 20 } }) =>
	async () => {
		if (contract_id === undefined || contract_id === null || contract_id === '') {
			return { rows: [], count: 0 };
		}

		const filter_by = { contract_id };
		if (status) filter_by.status = status;

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_contract_edit_history',
				filter_by,
				pagination: {
					page: pagination?.page || 1,
					per_page: pagination?.per_page || 20,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const data = response?.data || {};
			const rows = Array.isArray(data)
				? data
				: data.audits || data.history || data.rows || data.edit_history || [];
			const count =
				response?.meta?.count ||
				data.pagination?.total ||
				data.pagination?.total_count ||
				data.count ||
				rows.length ||
				0;
			return { rows, count };
		});
	};

/**
 * Approve/Reject Client Contract V2
 * @param {Object} params
 * @param {number} params.contract_id - Client contract ID
 * @param {number} params.approver_id - Approver user ID
 * @param {string} params.action - "approve" or "reject"
 * @param {string} params.comments - Comments (required if action = "approve")
 * @param {string} params.rejection_reason - Rejection reason (required if action = "reject")
 * @returns {Promise} API response
 */
export const approveClientContractV2 =
	({ contract_id, approver_id, action, comments, rejection_reason }) =>
	async () => {
		if (!contract_id) {
			toaster.error('Contract ID is required');
			return Promise.reject(new Error('Contract ID is required'));
		}

		if (!approver_id) {
			toaster.error('Approver ID is required');
			return Promise.reject(new Error('Approver ID is required'));
		}

		if (!action || !['approve', 'reject'].includes(action)) {
			toaster.error('Valid action is required (approve/reject)');
			return Promise.reject(new Error('Valid action is required'));
		}

		if (action === 'approve' && !comments) {
			toaster.error('Comments are required for approval');
			return Promise.reject(new Error('Comments are required for approval'));
		}

		if (action === 'reject' && !rejection_reason) {
			toaster.error('Rejection reason is required');
			return Promise.reject(new Error('Rejection reason is required'));
		}

		const requestData = {
			contract_id,
			approver_id,
			action,
		};

		if (action === 'approve') {
			requestData.comments = comments;
		} else {
			requestData.rejection_reason = rejection_reason;
		}

		const data = {
			update_type: 'handle_client_contract_approval',
			data: requestData,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			const message =
				action === 'approve'
					? 'Client contract approved and activated'
					: 'Client contract rejected';
			toaster.success(message);
			return res;
		});
	};

/**
 * Search Route Filters V2 - For dropdowns/autocomplete components
 * Endpoint: POST /api/v2/list_search
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria
 *   - project_id (integer, optional): Filter by project
 *   - vehicle_type_id (integer, optional): Filter by vehicle type
 *   - is_active (boolean, optional)
 * @param {number} params.limit - Maximum results (default: 50, min: 1, max: 100)
 * @returns {Promise} Object with { results: [{ id, label, value }], total }
 */
/**
 * Search Route Filters V2 - For dropdowns/autocomplete components
 * Endpoint: POST /api/v2/search
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria
 *   - project_id (number, required): Filter routes by project
 *   - vehicle_type_id (number, optional): Filter by vehicle type (maps to vehicle_type in API)
 *   - is_active (boolean, optional)
 *   - route_name (string, optional): Partial matching (case-insensitive)
 *   - route_code (string, optional)
 * @param {number} params.limit - Maximum results (default: 50, min: 1, max: 100)
 * @returns {Promise} Object with { results: [{ id, label, value }], total }
 */
export const searchRouteFiltersV2 =
	({ filter_by = {}, limit = 50 }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		// Validate and clamp limit
		const validatedLimit = Math.min(Math.max(limit || 50, 1), 1000);

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'route',
				filter_by: cleanFilterBy,
				pagination: {
					page: 1,
					per_page: validatedLimit,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			results: (response?.data?.routes || []).map((item) => ({
				id: item.id,
				label: item.route_name || item.route_code || `Route ${item.id}`,
				value: item.id,
			})),
			total: response?.data?.pagination?.total || 0,
		}));
	};

/**
 * Export Client Contract CSV V2
 * Endpoint: POST /api/v2/report
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria for export
 *   - client_id (integer, optional): Filter by client
 *   - project_id (integer, optional): Filter by project
 *   - is_active (boolean, optional): Filter by active status
 *   - approval_status (string, optional): Filter by approval status
 *   - search_term (string, optional): Search term
 * @returns {Promise} Object with { download_url, file_name, expires_at, total_records }
 */
export const exportClientContractCSV =
	({ filter_by = {} }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: '/report',
			data: {
				report_type: 'client_contract_export',
				format: 'csv',
				data: {
					filter_by: cleanFilterBy,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// Handle direct download URL response
			if (response?.data?.download_url) {
				toaster.success(
					`Export ready! Downloading ${response.data.file_name || 'file'}...`
				);
				// Automatically trigger download
				const link = document.createElement('a');
				link.href = response.data.download_url;
				link.download = response.data.file_name || 'client_contract_export.csv';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				return response.data;
			}
			// Handle async task response
			if (response?.data?.task_id) {
				toaster.info(
					`Export is being processed (Task ID: ${response.data.task_id}). You will be notified when ready.`
				);
				return response.data;
			}
			// Fallback - unexpected response format
			toaster.warning('Export response format unexpected. Please contact support.');
			return response?.data || {};
		});
	};

export default clientSliceV2.reducer;
